// R69 — THE POT, WHOLE, OUT OF THE MONOLITH — AND THE ONE TAP THAT EMPTIES IT.
//
// **THE EXTRACTION IS THE ONE R64 NAMED AND COULD NOT MAKE.** `CauldronExperience` renders
// `DeliveredReading`, which lived in App.tsx until R67, and a component in `src/components` cannot
// import back out of App.tsx. R67 moved the delivery block, so the reason is gone and the room's own
// three pieces — the door (`CauldronDoor.tsx`), the panel (`CauldronList.tsx`) and the experience
// that drives them — sit together for the first time.
//
// **AND THE MOVE PAID FOR ITSELF THE WAY THIS LINE ALWAYS DOES: THE THING THAT HAD TO MOVE IS THE
// THING NOTHING WAS WATCHING.** `clearAll` empties the whole pot. It is the one destroyer on this
// site that `scripts/r35-delete-ask.mjs` was structurally unable to see, because that fixture hunts
// destroyers BY NAME (`/[Dd]elete|[Rr]emove/`) and the two shapes it reads are
// `onClick={() => name(` and a prop bound to one of those names. `clearAll` is neither word and
// arrives through `onClear={clearAll}` on a plain `onClick={onClear}`, so it was on NEITHER side of
// R35's biconditional and could never have landed on either. A roster that cannot be reached by the
// thing it is a roster OF is R25's absent control one level up: there is no error state.
//
// **THE RULING, AND IT IS DELIBERATELY NOT R35's CONTROL.** R35 declared the Cauldron's single
// un-send cheap: the working comes out of the pot and nothing is destroyed. That reasoning is TRUE
// of every item a clear takes out, so `DeleteControl` would be the wrong instrument twice over — its
// icon is a bin on a row (this is a text button in an actions bar beside the primary one) and its
// sentence says "This can't be undone", which here would be a lie. What a clear really costs is the
// STACK: she assembled it working by working, across rooms, and rebuilding it means finding every
// one of them again. That is worth one question and no question is worth a destroyed thing, so it
// ASKS, in her own language, and the sentence says what actually happens to her workings.
//
// **THE ASK LIVES IN `CauldronList`, NOT HERE, AND THAT IS THE WHOLE POINT OF WHERE IT LIVES** — the
// panel is props in / tree out with no hook, so `scripts/r35-delete-ask.mjs` CALLS it in both
// clear states and reads the tree back. R35's own confirm can never be run for exactly the reason
// this one can: it holds its own state. The state is here; the decision is over there.
import { useEffect, useState } from "react";
import { t } from "../strings.js";
import { creationTitle, cleanCreationName } from "../creationName.js";
import { listCauldron, removeFromCauldron, clearCauldron, cauldronPotNote } from "../lib/cauldron-send";
import type { CauldronAnswer } from "../lib/cauldron-send";
import { postReadingToRoom, saveReadingNote, saveReadingState } from "../lib/saved-readings";
import CauldronList from "./CauldronList";
import type { CauldronItem } from "./CauldronList";
import { DeliveredReading } from "./DeliveredReading";
import type { AnyReport } from "./ReportBody";
import { AskLalaAboutButton } from "./AskLalaAboutButton";
import magicTileCauldronUrl from "../assets/magic-tile-cauldron.webp";

// MR-C — the Cauldron experience. Shows what the member sent (removable, persisted), and compiles it all
// into ONE coherent working document, delivered like every other working (shelf, My Room, PDF).
//
// `onRequireMembership` is typed with the ONE argument this room passes it. App.tsx's own handler takes
// an optional `LockInfo` second parameter and is assignable to this, so the type stays here rather than
// becoming a second copy of a shape App.tsx owns (R28/R29).
export function CauldronExperience({ isMember, onRequireMembership, language }: { isMember: boolean; onRequireMembership: (action: () => void) => void; language: string }) {
  // R64: NULL IS NOT AN EMPTY POT — W5-2/R39's third state, because "we could not read it" and "she
  // has sent nothing here" are two facts and this panel prints a different sentence for each.
  const [items, setItems] = useState<CauldronItem[] | null>(null);
  const [potNote, setPotNote] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<AnyReport | null>(null);
  const [save, setSave] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveNote, setSaveNote] = useState("");
  // R69: has she asked to empty the pot? The ask itself is drawn by the panel; only the answer to
  // "which of the two clear controls is on screen" lives up here, so the decision stays callable.
  const [clearAsk, setClearAsk] = useState(false);
  // N7-4: what SHE calls the working the Cauldron is about to draw together. Asked once, right here,
  // beside the button that makes it. Blank keeps the honest label "Cauldron Working".
  const [creationName, setCreationName] = useState("");
  void isMember;

  // R64: all three of the pot's routes read their ANSWER now, through `src/lib/cauldron-send.ts`
  // where the send already lived. A refused act NEVER paints — the pot only ever takes the list the
  // server just sent back — and it always SAYS SO. `setItems(null)` on a failed read is the round:
  // an unread pot is not an empty one, and the empty one's sentence tells her to go and send things
  // she has already sent.
  const potCopy = { signIn: t(language, "cauldron.signIn"), failed: t(language, "cauldron.potFailed") };
  const applyPot = (answer: CauldronAnswer<CauldronItem>, blankOnRefusal = false) => {
    if (answer.ok) { setItems(answer.items); setPotNote(""); return true; }
    if (blankOnRefusal) setItems(null);
    // FRONT 1 (her box, 2026-10-02): signed out, the READ is the membership page, not a sentence — the
    // same door a free member meets here through the read's 402, never "Sign in to work the Cauldron."
    if (blankOnRefusal && answer.kind === "sign-in") onRequireMembership(() => {});
    setPotNote(cauldronPotNote(answer, potCopy));
    return false;
  };
  async function refresh() {
    applyPot(await listCauldron<CauldronItem>(), true);
    setLoaded(true);
  }
  useEffect(() => { void refresh(); }, []);

  async function remove(id: string) { applyPot(await removeFromCauldron<CauldronItem>(id)); }
  // R69: the ask closes whatever the server answers, because the question has been answered either
  // way — a refusal is reported by `applyPot` (R64) and leaving the ask open over it would read as
  // the tap not having registered.
  async function clearAll() {
    setClearAsk(false);
    if (applyPot(await clearCauldron<CauldronItem>())) setReport(null);
  }

  function compile() {
    if (!items?.length) { setStatus(t(language, "cauldron.needItems")); return; }
    onRequireMembership(() => { void run(); });
  }
  async function run() {
    setBusy(true); setReport(null); setSave("idle"); setSaveNote("");
    const stages = [t(language, "cauldron.stage.gathering"), t(language, "cauldron.stage.drawing"), t(language, "cauldron.stage.order"), t(language, "working.writingOut")];
    let i = 0; setStatus(stages[0]);
    const timer = window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 2600);
    try {
      const r = await fetch("/api/cauldron/compile", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ creationName: cleanCreationName(creationName) }) });
      const d = await r.json().catch(() => ({}));
      if (r.status === 401) { setStatus(t(language, "cauldron.signIn")); return; }
      if (!r.ok || d?.ok === false || !d?.report) { setStatus(d?.message || t(language, "cauldron.failed")); return; }
      setReport(d.report as AnyReport); setStatus("");
    } catch { setStatus(t(language, "cauldron.failed")); }
    finally { window.clearInterval(timer); setBusy(false); }
  }
  async function saveToRoom() {
    if (!report) return;
    setSave("saving"); setSaveNote("");
    // N7-4: her name for the working, or the honest label. Never "You · Cauldron Working".
    const title = creationTitle("cauldron-working", (report as { creationName?: string }).creationName || creationName);
    const answer = await postReadingToRoom({ type: "cauldron-working", title, data: report });
    setSave(saveReadingState(answer)); setSaveNote(saveReadingNote(answer, { done: t(language, "save.done"), signIn: t(language, "save.signIn"), failed: t(language, "common.saveFailed") }));
  }

  return (
    <div className="specialty-hero cauldron-hero">
      <p className="eyebrow">Magic Room · The Cauldron</p>
      <h1 className="dream-title">The Cauldron</h1>
      <p className="spellbook-lede">{t(language, "cauldron.lede")}</p>

      <div className="cauldron-body">
        <img className="cauldron-body__img" src={magicTileCauldronUrl} alt="" aria-hidden="true" />
        <div className="cauldron-list">
          <CauldronList
            items={items} loaded={loaded} language={language} busy={busy} creationName={creationName} t={t}
            onCreationName={setCreationName} onRemove={remove} onCompile={compile}
            clearAsk={clearAsk} onClearAsk={setClearAsk} onClear={clearAll}
          />
          {/* R64: the pot's own answer, at panel width and beside the pot it is about. */}
          {potNote ? <p className="find-your-type__status" role="status">{potNote}</p> : null}
          {status ? <p className={`find-your-type__status${busy ? " is-live" : ""}`}>{status}</p> : null}
        </div>
      </div>

      {report ? (
        <DeliveredReading
          report={report} room="magic" language={language} save={save} saveNote={saveNote}
          onClose={() => { setReport(null); setSave("idle"); setSaveNote(""); }}
          onSaveMyRoom={saveToRoom}
          extras={<AskLalaAboutButton report={report} kind="reading" />}
        />
      ) : null}
    </div>
  );
}

export default CauldronExperience;
